/**
 * Date utility functions for handling mm/dd/yyyy format and timezone-safe display
 */

/**
 * Parse YYYY-MM-DD as local date (no UTC shift). Use for display and calculations.
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {{ year: number, month: number, day: number } | null} Local date parts or null
 */
export function parseLocalDateString(isoDate) {
  if (!isoDate || typeof isoDate !== 'string') return null;
  const match = String(isoDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) return null;
  const d = new Date(year, month - 1, day);
  if (d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return { year, month, day };
}

/**
 * Convert YYYY-MM-DD to Date in local time (avoids off-by-one when displaying)
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {Date | null}
 */
export function parseLocalDate(isoDate) {
  const p = parseLocalDateString(isoDate);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day);
}

/**
 * Convert mm/dd/yyyy to YYYY-MM-DD (ISO format for backend)
 * @param {string} mmddyyyy - Date in mm/dd/yyyy format
 * @returns {string} Date in YYYY-MM-DD format or null if invalid
 */
export function mmddyyyyToISO(mmddyyyy) {
  if (!mmddyyyy || !mmddyyyy.trim()) {
    return null;
  }
  
  // Remove any whitespace
  const cleaned = mmddyyyy.trim();
  
  // Match mm/dd/yyyy or mm-dd-yyyy format
  const match = cleaned.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (!match) {
    return null;
  }
  
  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  // Validate month (1-12)
  if (month < 1 || month > 12) {
    return null;
  }
  
  // Validate day (1-31, basic check)
  if (day < 1 || day > 31) {
    return null;
  }
  
  // Validate year (reasonable range)
  if (year < 1900 || year > 2100) {
    return null;
  }
  
  // Format as YYYY-MM-DD
  const monthStr = month.toString().padStart(2, '0');
  const dayStr = day.toString().padStart(2, '0');
  
  return `${year}-${monthStr}-${dayStr}`;
}

/**
 * Convert YYYY-MM-DD (ISO format) to mm/dd/yyyy
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {string} Date in mm/dd/yyyy format or empty string if invalid
 */
export function isoToMmddyyyy(isoDate) {
  if (!isoDate) return '';
  const p = parseLocalDateString(isoDate);
  if (!p) return '';
  const month = p.month.toString().padStart(2, '0');
  const day = p.day.toString().padStart(2, '0');
  return `${month}/${day}/${p.year}`;
}

/**
 * Validate mm/dd/yyyy format
 * @param {string} mmddyyyy - Date string to validate
 * @returns {object} { valid: boolean, error: string }
 */
export function validateMmddyyyy(mmddyyyy) {
  if (!mmddyyyy || !mmddyyyy.trim()) {
    return { valid: false, error: 'Date is required' };
  }
  
  const cleaned = mmddyyyy.trim();
  const match = cleaned.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  
  if (!match) {
    return { valid: false, error: 'Please enter date in mm/dd/yyyy format (e.g., 01/15/2024)' };
  }
  
  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  
  if (month < 1 || month > 12) {
    return { valid: false, error: 'Month must be between 01 and 12' };
  }
  
  if (day < 1 || day > 31) {
    return { valid: false, error: 'Day must be between 01 and 31' };
  }
  
  // Check if date is valid (e.g., not Feb 30)
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { valid: false, error: 'Invalid date' };
  }
  
  // Check if date is not too far in the future (allow up to 1 day in future for timezone issues)
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  // Allow dates up to tomorrow (for timezone edge cases) but warn if too far in future
  if (date > tomorrow) {
    return { valid: false, error: 'LMP date should be in the past. Please enter the first day of your last menstrual period.' };
  }
  
  return { valid: true, error: null };
}

/**
 * Convert date picker value (YYYY-MM-DD) to ISO format (same format, but ensures validity)
 * @param {string} datePickerValue - Date from date picker in YYYY-MM-DD format
 * @returns {string} Date in YYYY-MM-DD format or null if invalid
 */
export function datePickerToISO(datePickerValue) {
  if (!datePickerValue || !datePickerValue.trim()) {
    return null;
  }
  
  // Date picker already uses YYYY-MM-DD format, just validate it
  const match = datePickerValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }
  
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  
  // Validate
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900 || year > 2100) {
    return null;
  }
  
  // Check if date is valid
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  
  return datePickerValue; // Already in correct format
}

/**
 * Convert ISO format (YYYY-MM-DD) to date picker format (same format)
 * @param {string} isoDate - Date in YYYY-MM-DD format
 * @returns {string} Date in YYYY-MM-DD format for date picker
 */
export function isoToDatePicker(isoDate) {
  if (!isoDate) return '';
  const p = parseLocalDateString(isoDate);
  if (!p) return '';
  const month = p.month.toString().padStart(2, '0');
  const day = p.day.toString().padStart(2, '0');
  return `${p.year}-${month}-${day}`;
}

/**
 * Validate date picker value (YYYY-MM-DD format)
 * @param {string} datePickerValue - Date string to validate
 * @returns {object} { valid: boolean, error: string }
 */
export function validateDatePicker(datePickerValue) {
  if (!datePickerValue || !datePickerValue.trim()) {
    return { valid: false, error: 'Date is required' };
  }
  
  const match = datePickerValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return { valid: false, error: 'Please select a valid date' };
  }
  
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  
  if (month < 1 || month > 12) {
    return { valid: false, error: 'Invalid month' };
  }
  
  if (day < 1 || day > 31) {
    return { valid: false, error: 'Invalid day' };
  }
  
  // Check if date is valid (e.g., not Feb 30)
  const date = new Date(year, month - 1, day);
  if (date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { valid: false, error: 'Invalid date' };
  }
  
  // Check if date is not too far in the future
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date > tomorrow) {
    return { valid: false, error: 'LMP date should be in the past. Please enter the first day of your last menstrual period.' };
  }
  
  return { valid: true, error: null };
}

